import { useId, useState, type ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import type { QuoteRow } from "@/hooks/useQuotes";
import { useStaff, staffFullName, type StaffRow } from "@/hooks/useStaff";
import { cn } from "@/lib/utils";
import { isPickup } from "@/types/quotes";
import { useQuoteServersEditor, useSetQuoteDriver } from "./useQuoteActions";

const NONE = "";
const selectCls = "flex h-8 w-auto min-w-[180px] rounded-md border border-input bg-background px-2 text-sm text-foreground disabled:opacity-50";

function Shell({ children }: { children: ReactNode }) {
  return (
    <section data-section="crew" className="rounded-[10px] border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3.5 py-2.5">
        <h3 className="font-display text-[15px] font-bold">Crew</h3>
        <span className="text-[12.5px] text-muted-foreground">Staff only</span>
      </div>
      <div className="px-3.5 py-3">{children}</div>
    </section>
  );
}

/** A driver missing from the active pool: inactive, no longer a driver, or not loaded (#1010). */
function missingDriverLabel(driverId: string, pool: StaffRow[], allStaff: StaffRow[] | undefined): string | null {
  if (pool.some((s) => s.id === driverId)) return null;
  const s = allStaff?.find((x) => x.id === driverId);
  if (!s) return "Current driver";
  return staffFullName(s) + (!s.is_active ? " (inactive)" : s.roles?.includes("driver") ? "" : " (not a driver)");
}

/** Driver and servers, saved as soon as they change (#1559). */
function CrewPickers({ quote }: { quote: QuoteRow }) {
  const id = useId();
  const { data: driverPool = [] } = useStaff({ activeOnly: true, role: "driver" });
  const { data: serverPool = [] } = useStaff({ activeOnly: true, role: "server" });
  // Only to label a driver who left the pool, so a quote without one reads no extra rows (#1390).
  const { data: allStaff } = useStaff({ activeOnly: false, enabled: !!quote.driver_id });
  const driver = useSetQuoteDriver(quote.id);
  const servers = useQuoteServersEditor(quote.id);
  // Shown at once; keyed by the saved driver below, so a refetch starts it over.
  const [driverId, setDriverId] = useState(quote.driver_id ?? NONE);
  const missing = driverId ? missingDriverLabel(driverId, driverPool, allStaff) : null;

  const toggleServer = (staffId: string) => {
    const next = servers.serverIds.includes(staffId) ? servers.serverIds.filter((s) => s !== staffId) : [...servers.serverIds, staffId];
    void servers.save(next);
  };

  return (
    <div className="flex flex-wrap items-end gap-x-[18px] gap-y-2.5">
      <div className="grid gap-[3px] text-[12px] text-muted-foreground">
        <label htmlFor={`${id}-driver`}>Driver</label>
        <select
          id={`${id}-driver`}
          value={driverId}
          disabled={driver.saving}
          onChange={(e) => {
            const prev = driverId;
            setDriverId(e.target.value);
            void driver.setDriver(e.target.value || null).then((ok) => { if (!ok) setDriverId(prev); });
          }}
          className={selectCls}
        >
          <option value={NONE}>Not assigned</option>
          {driverId && missing && <option value={driverId}>{missing}</option>}
          {driverPool.map((s) => <option key={s.id} value={s.id}>{staffFullName(s)}</option>)}
        </select>
      </div>
      <div className="grid gap-[3px] text-[12px] text-muted-foreground">
        <span id={`${id}-servers`}>Servers</span>
        {!servers.ready ? (
          <span>{servers.query.isError ? "Couldn't load this quote's servers. Reload to change them." : "Loading servers…"}</span>
        ) : serverPool.length === 0 ? (
          <span>No active servers. Add one from /admin/staff.</span>
        ) : (
          <div role="group" aria-labelledby={`${id}-servers`} className="flex flex-wrap gap-1.5">
            {serverPool.map((s) => {
              const on = servers.serverIds.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={on}
                  disabled={servers.saving}
                  onClick={() => toggleServer(s.id)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-xs transition-colors",
                    on ? "border-primary bg-primary text-primary-foreground" : "bg-muted/50 text-muted-foreground hover:bg-muted",
                  )}
                >
                  {staffFullName(s)}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <span className="text-[12.5px] text-muted-foreground">Saves right away.</span>
    </div>
  );
}

/**
 * Crew (#1559): not for Pickup, and behind the staff flag. Nothing while the
 * flags load; a hint when they failed, rather than hiding the crew (#648).
 */
export function CrewSection({ quote }: { quote: QuoteRow }) {
  const { featureFlags, featureFlagsStatus } = useAuth();
  if (isPickup(quote.service_type, quote.fulfillment_type)) return null;
  if (featureFlagsStatus === "error") {
    return (
      <Shell>
        <p className="text-xs text-muted-foreground">
          Couldn't load feature settings, so crew assignments can't be shown. Reload the page to try again.
        </p>
      </Shell>
    );
  }
  if (featureFlags?.staff !== true) return null;
  return (
    <Shell>
      <CrewPickers key={quote.driver_id ?? NONE} quote={quote} />
    </Shell>
  );
}
